import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { X } from "lucide-react";

/**
 * N/A justification modal — Brief 2 sub-task 3 (live counter, 20 char min).
 */
export default function NAModal({ open, onClose, docLabel, minLength, maxLength, onSubmit, submitting, error, initial }) {
  const [text, setText] = useState(initial || "");
  const len = text.trim().length;
  const tooShort = len < minLength;
  const overMax = text.length > maxLength;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="na-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center px-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-3xl border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.01] backdrop-blur-2xl p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} aria-label="Close" data-testid="na-modal-close" className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06]">
              <X className="h-4 w-4" />
            </button>
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300">— Mark as N/A</div>
            <h3 className="font-display mt-2 text-2xl text-white tracking-tight">{docLabel}</h3>
            <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed">
              Why doesn't this apply to your company? Your underwriter will see this verbatim.
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={maxLength}
              rows={4}
              data-testid="na-justification-input"
              placeholder="Briefly explain why this doesn't apply — e.g. 'Company is less than 6 months old so no audited accounts exist yet.'"
              className="mt-4 w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 text-[14px] text-white placeholder:text-zinc-600 resize-y min-h-[110px]"
            />
            <div className="mt-2 flex items-center justify-between text-[11.5px]">
              <span data-testid="na-counter" className={tooShort ? "text-amber-300" : "text-emerald-300"}>
                {len} / {minLength} minimum{len > minLength ? "  ✓" : ""}
              </span>
              <span className="text-zinc-600">{text.length} / {maxLength}</span>
            </div>
            {error && (
              <div data-testid="na-error" className="mt-3 rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-4 py-3">
                {error}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={onClose} className="rounded-full border border-white/[0.10] hover:bg-white/[0.04] text-zinc-200 px-4 py-2 text-[13px]">Cancel</button>
              <button
                onClick={() => onSubmit(text.trim())}
                disabled={tooShort || overMax || submitting}
                data-testid="na-submit-btn"
                className="rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2 text-[13px] font-medium"
              >
                {submitting ? "Saving…" : "Mark as N/A"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
